import { useCallback, useState, type RefObject } from 'react';
import { useEventListener, useIsomorphicLayoutEffect } from './useEventListener';

type Position = { top: number; left: number };

type UseAnchoredPositionOptions = {
  /** Width of the positioned panel, used to keep it inside the viewport. Defaults to 365. */
  panelWidth?: number;

  /** Minimum gap between the panel and the viewport edges. Defaults to 8. */
  viewportMargin?: number;

  /** Vertical gap between the anchor's bottom and the panel's top. Defaults to 4. */
  anchorGap?: number;
};

// Listen in the capture phase so scrolling a nested overflow container (which doesn't
// bubble scroll events) still repositions the panel. Stable identity avoids re-attaching.
const SCROLL_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true };

/**
 * Computes a viewport-fixed position just below `anchorRef`, clamped inside the viewport.
 *
 * Used for popovers rendered in a portal so they escape a horizontally scrollable
 * (overflow-clipping) ancestor. The position is recomputed on scroll and resize while `active`.
 */
export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null> | undefined,
  active: boolean,
  options: UseAnchoredPositionOptions = {},
): Position | null {
  const { panelWidth = 365, viewportMargin = 8, anchorGap = 4 } = options;

  const [position, setPosition] = useState<Position | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!active || !anchor) {
      setPosition(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const maxLeft = Math.max(viewportMargin, window.innerWidth - panelWidth - viewportMargin);
    const left = Math.min(Math.max(rect.left, viewportMargin), maxLeft);
    setPosition({ top: rect.bottom + anchorGap, left });
  }, [active, anchorRef, panelWidth, viewportMargin, anchorGap]);

  useIsomorphicLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEventListener('scroll', updatePosition, undefined, SCROLL_LISTENER_OPTIONS);
  useEventListener('resize', updatePosition);

  return position;
}
